import React, { ReactNode, useMemo } from "react";
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { ApolloLink } from "apollo-link";
import { RetryLink } from "apollo-link-retry";
import { createUploadLink } from "apollo-upload-client";
import { OperationDefinitionNode } from "graphql";
import { every, get } from "lodash";
import { ApolloProvider as Provider } from "react-apollo";
import {
  isOktaEnabled,
  redirectToLogin,
  useGetAccessToken,
  useHandleAuthenticationError,
} from "auth/utils/oktaAuth";
import { ONE_SECOND } from "core/constants";
import introspectionQueryResultData from "core/queries/__generated__/fragmentTypes.json";

interface ApolloProviderProps {
  children: ReactNode;
}

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

export const ApolloProvider = ({ children }: ApolloProviderProps) => {
  const authToken = useGetAccessToken();
  const { handleAuthenticationError } = useHandleAuthenticationError();
  const client = useMemo(() => {
    const httpLink = createUploadLink({
      uri: GRAPHQL_HOST,
      credentials: "include",
    });
    const retryLink = new RetryLink({
      delay: {
        initial: ONE_SECOND,
        max: Infinity,
        jitter: true,
      },
      attempts: {
        max: 5,
        retryIf: (error, _operation) => {
          const allQueriesOnly = every(
            _operation.query.definitions,
            (definition: OperationDefinitionNode) =>
              definition.operation === "query",
          );
          return !!error && allQueriesOnly;
        },
      },
    });
    const authMiddleware = new ApolloLink((operation, forward) => {
      if (!forward) return null;

      // If there's no auth token, skip the query and redirect to /login
      if (!authToken) {
        redirectToLogin();
        return null;
      }

      // If there is an auth token, set appropriate headers
      operation.setContext({
        headers: {
          authorization: `Bearer ${authToken}`,
          SONDER_APP: "dispatch-dashboard",
          "x-sndr-auth-version": isOktaEnabled ? 2 : 1,
        },
      });

      return forward(operation).map(response => {
        if (
          ["NOT_AUTHENTICATED", "NOT_AUTHORIZED"].includes(
            get(response, "errors[0].extensions.code", null),
          )
        ) {
          // Failed auth, clear the invalid token and redirect to /login
          handleAuthenticationError();
        }
        return response;
      });
    });

    return new ApolloClient({
      link: ApolloLink.from([authMiddleware, retryLink, httpLink]),
      cache: new InMemoryCache({ fragmentMatcher }),
    });
  }, [authToken]);
  return <Provider client={client}>{children}</Provider>;
};
